import React, { useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import { MONTH_LABELS } from '../../utils/ragUtils';

// Chart.js v2 — no register() needed

export default function Beacon4BlendedKpiLineChart({ series, config, sliceFrom, sliceTo }) {
  const chartData = useMemo(() => {
    if (!series?.data?.length) return null;

    const allData = series.data;
    const from = sliceFrom ?? 0;
    const to   = sliceTo   ?? allData.length - 1;
    const data = allData.slice(from, to + 1);

    // Find last actual (non-projected) index in sliced data
    const lastActualIdx = data.reduce((acc, d, i) => (!d.isProjected ? i : acc), -1);

    const labels       = data.map(d => `${MONTH_LABELS[d.month]} ${String(d.year).slice(2)}`);
    const actualValues = data.map(d => d.isProjected ? null : d.value);
    const projValues   = data.map((d, i) => {
      if (!d.isProjected) {
        const next = data[i + 1];
        return next?.isProjected ? d.value : null;
      }
      return d.value;
    });
    const greenLine = data.map(d => d.ragGreen ?? null);
    const redLine   = data.map(d => d.ragRed   ?? null);

    // Last actual month marker — red dot
    const markerData = data.map((d, i) => i === lastActualIdx ? d.value : null);

    return {
      labels,
      datasets: [
        {
          label:           'Actual',
          data:            actualValues,
          borderColor:     '#378ADD',
          backgroundColor: 'rgba(55,138,221,0.07)',
          borderWidth:     2,
          pointRadius:     0,
          lineTension:     0.1,
          fill:            true,
        },
        {
          label:                'Projection',
          data:                 projValues,
          borderColor:          '#378ADD',
          backgroundColor:      'rgba(55,138,221,0.03)',
          borderWidth:          1.5,
          borderDash:           [5, 3],
          pointRadius:          0,
          lineTension:          0.1,
          fill:                 true,
        },
        {
          label:      'Green threshold',
          data:       greenLine,
          borderColor:'#4CAF50',
          borderWidth: 1,
          borderDash:  [3, 4],
          pointRadius: 0,
          fill:        false,
          spanGaps:    true,
        },
        {
          label:      'Red threshold',
          data:       redLine,
          borderColor:'#E24B4A',
          borderWidth: 1,
          borderDash:  [3, 4],
          pointRadius: 0,
          fill:        false,
          spanGaps:    true,
        },
        // Last actual month marker
        {
          label:                'Last actual',
          data:                 markerData,
          borderColor:          '#E24B4A',
          backgroundColor:      '#E24B4A',
          borderWidth:          2,
          pointRadius:          markerData.map(v => v != null ? 5 : 0),
          pointHoverRadius:     7,
          pointStyle:           'circle',
          lineTension:          0,
          fill:                 false,
          spanGaps:             false,
          showLine:             false,
        },
      ],
    };
  }, [series, config, sliceFrom, sliceTo]);

  const options = useMemo(() => ({
    responsive:          true,
    maintainAspectRatio: false,
    animation:           { duration: 300 },
    legend:              { display: false },
    tooltips: {
      mode:            'index',
      intersect:       false,
      backgroundColor: 'rgba(255,255,255,0.98)',
      borderColor:     'rgba(0,0,0,0.1)',
      borderWidth:     1,
      titleFontColor:  '#8fa3bc',
      bodyFontColor:   '#1a2233',
      titleFontFamily: 'IBM Plex Mono, monospace',
      bodyFontFamily:  'IBM Plex Mono, monospace',
      titleFontSize:   10,
      bodyFontSize:    11,
      xPadding:        10,
      yPadding:        10,
      callbacks: {
        label: (item, data) => {
          const label = data.datasets[item.datasetIndex].label || '';
          if (label === 'Last actual') return null;
          return item.yLabel != null ? ` ${label}: ${Number(item.yLabel).toFixed(config?.decimals ?? 2)}` : null;
        },
      },
    },
    scales: {
      xAxes: [{
        gridLines: { color: 'rgba(0,0,0,0.04)', lineWidth: 0.5 },
        ticks: { fontColor: 'rgba(0,0,0,0.3)', fontFamily: 'IBM Plex Mono, monospace', fontSize: 8, maxTicksLimit: 8, maxRotation: 0 },
      }],
      yAxes: [{
        gridLines: { color: 'rgba(0,0,0,0.04)', lineWidth: 0.5 },
        ticks: { fontColor: 'rgba(0,0,0,0.3)', fontFamily: 'IBM Plex Mono, monospace', fontSize: 8, maxTicksLimit: 4, callback: v => Number(v).toFixed(config?.decimals ?? 2) },
      }],
    },
  }), [config]);

  if (!chartData) return null;

  return (
    <div style={{ position: 'relative', height: 160 }}>
      <Line data={chartData} options={options} />
    </div>
  );
}